import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Bell, Pencil, Plus, Trash2, X } from "lucide-react";
import DarkBlueButton from "../atoms/buttons/DarkBlueButton";
import { useUserData } from "../../lib/contexts/UserContext";
import { useFeatureFlags } from "../../lib/contexts/FeatureFlagContext";
import { allowedCountryCodes, isValidNationalLength } from "../../lib/utils/countryCodes";
import {
  BookingAlertEvent,
  BookingAlertRule,
  createBookingAlertRule,
  deleteBookingAlertRule,
  getBookingAlerts,
  updateBookingAlertRule,
} from "../../lib/apis/bookingAlerts";

/**
 * Admin Panel > TrueCare > Booking Updates.
 *
 * Each update is a rule: the booking statuses that fire it (Booked / Walk-in /
 * Cancelled / Rescheduled) and the staff numbers that get a WhatsApp the moment it happens
 * (template booking_alert_v1, sent from the hospital's number). Same row layout
 * as Scheduled Messages: edit, delete, on/off.
 *
 * Stored in booking_alert_rule (one row per update). MasterAdmin only, and only
 * while hospital_feature_flags.booking_alerts is on -- the Admin Panel hides the
 * section and the API refuses everything else regardless.
 */
const STATUSES: { key: BookingAlertEvent; label: string; hint: string }[] = [
  { key: "booking", label: "Booked", hint: "new appointment or follow-up, from WhatsApp, online or the desk" },
  { key: "walkin", label: "Walk-in", hint: "walk-in added at the clinic" },
  { key: "cancel", label: "Cancelled", hint: "cancelled by the patient or staff" },
  { key: "reschedule", label: "Rescheduled", hint: "moved to another date or time, from any screen" },
];
const STATUS_LABEL: Record<BookingAlertEvent, string> = {
  booking: "Booked",
  walkin: "Walk-in",
  cancel: "Cancelled",
  reschedule: "Rescheduled",
};
const MAX_PHONES = 20;

const fmtPhone = (digits: string) => {
  const d = String(digits);
  return d.length > 10 ? `+${d.slice(0, d.length - 10)} ${d.slice(-10)}` : `+${d}`;
};

// Same toggle / chip look as the rest of the Admin Panel and Scheduled Messages.
const toggleButtonClass = `group peer bg-white rounded-full duration-300 w-14 h-6 ring-[1.5px] ring-[#A6A6A6]
    after:duration-300 after:bg-[#A6A6A6] peer-checked:after:bg-[#335FE4] peer-checked:ring-[#335FE4]
    after:rounded-full after:absolute after:h-4 after:w-4 after:top-[4px] after:left-1
    after:flex after:justify-center after:items-center peer-checked:after:translate-x-8 peer-hover:after:scale-95`;
const chipClass = (active: boolean) =>
  `px-3 py-1.5 text-xs md:text-sm rounded-full border transition-colors ${
    active
      ? "border-[#335FE4] bg-blue-50 text-[#335FE4] font-medium"
      : "border-gray-200 bg-white text-gray-600 hover:border-gray-400"
  }`;
const statusBadgeClass = (e: BookingAlertEvent) =>
  e === "cancel"
    ? "text-red-700 bg-red-50 border-red-200"
    : e === "walkin"
      ? "text-amber-700 bg-amber-50 border-amber-200"
      : e === "reschedule"
        ? "text-purple-700 bg-purple-50 border-purple-200"
        : "text-blue-700 bg-blue-50 border-blue-200";

type Draft = { label: string; events: BookingAlertEvent[]; phones: string[] };
const EMPTY: Draft = { label: "", events: [], phones: [] };

const BookingAlertsSection = ({ hospitalId }: { hospitalId: string }) => {
  const { userData } = useUserData();
  const { phone_country_codes } = useFeatureFlags();
  const countryOptions = allowedCountryCodes(phone_country_codes);
  const canEdit = userData?.role === "MasterAdmin";

  const [enabled, setEnabled] = useState(false);
  const [rules, setRules] = useState<BookingAlertRule[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [code, setCode] = useState(countryOptions[0]);
  const [number, setNumber] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  // Flags may load after the first render: a code the hospital doesn't offer falls
  // back to its first one (same rule as Create Appointment).
  const activeCode = countryOptions.includes(code) ? code : countryOptions[0];

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await getBookingAlerts(hospitalId);
      setEnabled(!!res?.data?.result?.enabled);
      setRules(res?.data?.result?.rules ?? []);
    } catch (error) {
      console.error("Error loading booking updates:", error);
      toast.error("Could not load booking updates.");
    } finally {
      setIsLoading(false);
    }
  }, [hospitalId]);

  useEffect(() => {
    if (canEdit) load();
  }, [load, canEdit]);

  // Validator errors arrive as an array of { msg }; everything else as a string.
  const apiError = (error: any, fallback: string) => {
    const data = error?.response?.data;
    const detail = Array.isArray(data?.error)
      ? data.error.map((e: any) => e?.msg || e?.message).filter(Boolean).join(". ")
      : data?.error || data?.message;
    toast.error(detail || fallback);
  };

  const openCreate = () => {
    setEditingId(null);
    setDraft(EMPTY);
    setNumber("");
    setShowForm(true);
  };
  const openEdit = (rule: BookingAlertRule) => {
    setEditingId(rule.rule_id);
    setDraft({ label: rule.label || "", events: rule.events, phones: rule.phones });
    setNumber("");
    setShowForm(true);
  };
  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setDraft(EMPTY);
    setNumber("");
  };

  const toggleStatus = (e: BookingAlertEvent) =>
    setDraft((d) => ({
      ...d,
      events: d.events.includes(e) ? d.events.filter((x) => x !== e) : [...d.events, e],
    }));

  // Returns the draft's numbers plus the one still in the input box (if valid), so
  // Save works even when the last number wasn't "added" first.
  const addNumber = (): string[] | null => {
    const national = number.replace(/\D/g, "");
    if (!national) return draft.phones;
    if (!isValidNationalLength(activeCode, national)) {
      toast.error("Enter a valid phone number.");
      return null;
    }
    const full = `${activeCode}${national}`;
    if (draft.phones.includes(full)) {
      setNumber("");
      return draft.phones;
    }
    if (draft.phones.length >= MAX_PHONES) {
      toast.error(`Up to ${MAX_PHONES} numbers per update.`);
      return null;
    }
    const phones = [...draft.phones, full];
    setDraft((d) => ({ ...d, phones }));
    setNumber("");
    return phones;
  };

  const removeNumber = (p: string) =>
    setDraft((d) => ({ ...d, phones: d.phones.filter((x) => x !== p) }));

  const handleSave = async () => {
    const phones = addNumber();
    if (!phones) return;
    if (!draft.events.length) return toast.error("Pick at least one booking status.");
    if (!phones.length) return toast.error("Add at least one number.");
    const body = { label: draft.label.trim() || null, events: draft.events, phones };
    setIsSaving(true);
    try {
      if (editingId) await updateBookingAlertRule(hospitalId, editingId, body);
      else await createBookingAlertRule(hospitalId, body);
      toast.success(editingId ? "Booking update saved." : "Booking update created.");
      closeForm();
      await load();
    } catch (error) {
      apiError(error, "Could not save the booking update.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggle = async (rule: BookingAlertRule) => {
    setIsSaving(true);
    try {
      await updateBookingAlertRule(hospitalId, rule.rule_id, { enabled: !rule.enabled });
      await load();
    } catch (error) {
      apiError(error, "Could not switch it.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (rule: BookingAlertRule) => {
    setIsSaving(true);
    try {
      await deleteBookingAlertRule(hospitalId, rule.rule_id);
      setConfirmDeleteId(null);
      if (editingId === rule.rule_id) closeForm();
      await load();
    } catch (error) {
      apiError(error, "Could not delete it.");
    } finally {
      setIsSaving(false);
    }
  };

  const ruleName = (rule: BookingAlertRule) =>
    rule.label || rule.events.map((e) => STATUS_LABEL[e]).join(", ");
  const onCount = rules.filter((r) => r.enabled).length;

  if (!canEdit) return null;

  return (
    <div className="flex flex-col mt-4 p-2 md:p-4">
      <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-3">
        <div className="flex flex-col mt-2">
          <h1 className="text-sm md:text-md font-semibold">
            Booking Updates
            <span className="ml-2 align-middle text-[10px] font-semibold uppercase tracking-wide text-blue-700 bg-blue-50 border border-blue-200 rounded px-1.5 py-0.5">
              New
            </span>
          </h1>
          <p className="text-xs md:text-sm text-gray-600">
            Send a WhatsApp to your staff the moment a booking is made, a walk-in is added, or a booking is cancelled or rescheduled.
          </p>
        </div>
        {enabled && (
          <div className="flex items-center gap-3 md:mt-2 shrink-0">
            <span className="text-xs md:text-sm text-gray-600">
              <span className="font-semibold text-gray-900">{onCount}</span> on · {rules.length} total
            </span>
            {!showForm && <DarkBlueButton name="Add Update" disabled={isSaving} handleClick={openCreate} />}
          </div>
        )}
      </div>

      {!isLoading && !enabled && (
        <p className="mt-3 text-xs md:text-sm text-gray-500">
          Booking updates are not switched on for this hospital. Please contact DocTrue.
        </p>
      )}

      {showForm && enabled && (
        <div className="mt-4 rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
            <div className="text-sm font-semibold text-gray-900">
              {editingId ? "Edit booking update" : "New booking update"}
            </div>
            <button className="text-xs text-gray-500 hover:text-gray-800" onClick={closeForm} disabled={isSaving}>
              Close
            </button>
          </div>

          <div className="flex flex-col gap-5 p-4">
            <label className="flex flex-col gap-2">
              <span className="text-sm font-medium text-gray-800">
                Name <span className="font-normal text-gray-400">(optional, only staff see it)</span>
              </span>
              <input
                className="rounded-lg px-3 py-2 border border-gray-300 bg-white text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-200 md:max-w-md"
                placeholder="e.g. Reception desk"
                value={draft.label}
                maxLength={80}
                onChange={(e) => setDraft({ ...draft, label: e.target.value })}
              />
            </label>

            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium text-gray-800">When the booking is</span>
              <div className="flex flex-wrap gap-2">
                {STATUSES.map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    className={chipClass(draft.events.includes(s.key))}
                    onClick={() => toggleStatus(s.key)}
                    title={s.hint}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium text-gray-800">
                Send to <span className="font-normal text-gray-400">(up to {MAX_PHONES} numbers)</span>
              </span>
              <div className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-300 bg-white px-2 py-1.5 focus-within:ring-2 focus-within:ring-blue-200">
                {draft.phones.map((p) => (
                  <span
                    key={p}
                    className="inline-flex items-center gap-1 rounded-full bg-blue-50 border border-blue-200 text-blue-800 text-xs px-2.5 py-1"
                  >
                    {fmtPhone(p)}
                    <button
                      type="button"
                      onClick={() => removeNumber(p)}
                      className="text-blue-400 hover:text-red-600"
                      aria-label={`Remove ${fmtPhone(p)}`}
                    >
                      <X size={12} />
                    </button>
                  </span>
                ))}
                <select
                  value={activeCode}
                  onChange={(e) => setCode(e.target.value)}
                  className="h-8 rounded-md border border-gray-200 bg-gray-50 px-1.5 text-sm"
                  aria-label="Country code"
                >
                  {countryOptions.map((c) => (
                    <option key={c} value={c}>
                      +{c}
                    </option>
                  ))}
                </select>
                <input
                  value={number}
                  onChange={(e) => setNumber(e.target.value.replace(/\D/g, ""))}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === ",") {
                      e.preventDefault();
                      addNumber();
                    }
                  }}
                  placeholder={draft.phones.length ? "Add another number" : "Phone number"}
                  inputMode="numeric"
                  className="h-8 flex-1 min-w-[140px] text-sm outline-none"
                />
                <button
                  type="button"
                  onClick={() => addNumber()}
                  className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md text-sm text-[#335FE4] hover:bg-blue-50"
                >
                  <Plus size={14} /> Add
                </button>
              </div>
              <p className="text-[11px] text-gray-500">Press Enter after each number.</p>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-gray-100 px-4 py-3">
            <button
              className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 hover:bg-gray-100"
              onClick={closeForm}
              disabled={isSaving}
            >
              Cancel
            </button>
            <DarkBlueButton
              name={editingId ? "Save changes" : "Save update"}
              disabled={isSaving}
              handleClick={handleSave}
            />
          </div>
        </div>
      )}

      {enabled && (
        <div className="mt-4">
          {isLoading ? (
            <p className="text-xs md:text-sm text-gray-500">Loading…</p>
          ) : rules.length === 0 ? (
            <div className="rounded-xl border border-dashed border-gray-300 px-4 py-8 text-center">
              <Bell size={22} className="mx-auto text-gray-300" />
              <p className="mt-2 text-sm text-gray-600">No booking updates yet.</p>
              <p className="text-xs text-gray-500">Click "Add Update" to create the first one.</p>
            </div>
          ) : (
            <div className="flex flex-col divide-y divide-gray-100 rounded-xl border border-gray-200 bg-white">
              {rules.map((rule) => (
                <div
                  key={rule.rule_id}
                  className={`flex flex-col md:flex-row md:items-center gap-3 px-3 py-3 ${
                    editingId === rule.rule_id ? "bg-blue-50/50" : ""
                  }`}
                >
                  <div className={`flex flex-col min-w-0 flex-1 ${rule.enabled ? "" : "opacity-60"}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-gray-900">{ruleName(rule)}</span>
                      {rule.events.map((e) => (
                        <span
                          key={e}
                          className={`text-[10px] uppercase tracking-wide border rounded px-1.5 py-0.5 ${statusBadgeClass(e)}`}
                        >
                          {STATUS_LABEL[e]}
                        </span>
                      ))}
                    </div>
                    <p className="text-xs text-gray-600 mt-1 break-words">
                      {rule.phones.map(fmtPhone).join(", ")}
                    </p>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    {confirmDeleteId === rule.rule_id ? (
                      <>
                        <span className="text-xs text-gray-600">Delete?</span>
                        <button
                          className="px-2.5 py-1 text-xs rounded-lg bg-red-600 text-white disabled:opacity-50"
                          disabled={isSaving}
                          onClick={() => handleDelete(rule)}
                        >
                          Delete
                        </button>
                        <button
                          className="px-2.5 py-1 text-xs rounded-lg border border-gray-300"
                          onClick={() => setConfirmDeleteId(null)}
                        >
                          Keep
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                          disabled={isSaving}
                          onClick={() => openEdit(rule)}
                          title="Edit"
                          aria-label={`Edit ${ruleName(rule)}`}
                        >
                          <Pencil size={15} />
                        </button>
                        <button
                          className="p-1.5 rounded-lg text-gray-500 hover:bg-red-50 hover:text-red-600"
                          disabled={isSaving}
                          onClick={() => setConfirmDeleteId(rule.rule_id)}
                          title="Delete"
                          aria-label={`Delete ${ruleName(rule)}`}
                        >
                          <Trash2 size={15} />
                        </button>
                      </>
                    )}
                    <span
                      className={`ml-2 w-6 text-right text-xs font-medium ${
                        rule.enabled ? "text-[#335FE4]" : "text-gray-400"
                      }`}
                    >
                      {rule.enabled ? "On" : "Off"}
                    </span>
                    <label className="flex relative cursor-pointer" title={rule.enabled ? "Switch off" : "Switch on"}>
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={rule.enabled}
                        disabled={isSaving}
                        onChange={() => handleToggle(rule)}
                      />
                      <div className={toggleButtonClass} />
                    </label>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BookingAlertsSection;
